import { memo, ReactElement, useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import useSeasons from '../../dataHooks/useSeasons';
import Season from '../../types/Season';
import Hourglass from '../common/Hourglass';

interface SeasonSelectorProps {
  value?: string | null;
  defaultValue?: string | null;
  leagueId?: string | null;
  orgId?: string | null;
  onChange: (season: Season | undefined) => void;
  disabled?: boolean;
  helperText?: string;
  required?: boolean;
  sx?: SxProps<Theme>;
  variant?: 'select' | 'chip';
}

export default memo(function SeasonSelector(props: SeasonSelectorProps): ReactElement {
  const { value, onChange, disabled = false, helperText, required = false, leagueId, orgId, sx, variant = 'select' } = props;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const { seasons, isSeasonsLoading, seasonsError } = useSeasons({
    includeCatchAll: true,
    leagueId: leagueId,
    orgId: orgId,
  });

  useEffect(() => {
    return () => {
      // enables the react-firebase-hook to do proper unloading
    };
  }, [seasons, isSeasonsLoading, seasonsError]);

  // Don't render the select until seasons are loaded
  if (isSeasonsLoading) {
    return <Hourglass />;
  }

  if (seasonsError) {
    return <Alert severity="error">Error loading seasons: {seasonsError.message}</Alert>;
  }

  // Determine the current value for the controlled component
  // Validate that the value is in the current seasons list to avoid MUI Select out-of-range error
  const isValueValid = value && seasons?.some((s) => s.itemId === value);
  const currentValue = isValueValid ? value : '';

  if (variant === 'chip') {
    const selectedSeason = seasons?.find((s) => s.itemId === currentValue);
    const hasMultiple = seasons && seasons.length > 1;

    if (!hasMultiple) {
      return (
        <Typography variant="labelLarge" sx={sx}>
          {selectedSeason?.name ?? seasons?.[0]?.name ?? 'Season'}
        </Typography>
      );
    }

    return (
      <>
        <Chip
          label={selectedSeason?.name ?? 'Season'}
          deleteIcon={<ArrowDropDownIcon />}
          onDelete={(e) => setMenuAnchor(e.currentTarget.parentElement)}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          variant="outlined"
          disabled={disabled}
          sx={sx}
        />
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {seasons?.map((season) =>
            season ? (
              <MenuItem
                key={season.itemId}
                selected={season.itemId === currentValue}
                onClick={() => {
                  onChange(season);
                  setMenuAnchor(null);
                }}
              >
                {season.name}
              </MenuItem>
            ) : null
          )}
        </Menu>
      </>
    );
  }

  return (
    <TextField
      select
      required={required}
      fullWidth
      label="Season"
      value={currentValue}
      error={required && !currentValue}
      disabled={disabled || isSeasonsLoading}
      helperText={helperText || (required && !currentValue ? 'Season is required' : '')}
      onChange={(e) => {
        const newValue = e.target.value || null;
        const season = seasons?.find((s) => s.itemId === newValue);
        onChange(season);
      }}
      sx={sx}
    >
      {seasons?.map((season) =>
        season ? (
          <MenuItem key={season.itemId} value={season.itemId}>
            {season.name}
          </MenuItem>
        ) : null
      )}
    </TextField>
  );
});
